import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Pagination, PaginationContent, PaginationItem, PaginationLink,
  PaginationNext, PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Search, TrendingUp, DollarSign, Target, Layers, Zap,
  BarChart3, PieChart, Lightbulb, Rocket, Factory,
  ArrowUpRight, ArrowDownRight, Calendar, Users,
  Sparkles, Eye, GitMerge, Loader2, CheckCircle2, AlertTriangle, Download,
  LayoutGrid, List, GitCompare, FileCheck2, FileX2, Paperclip, ShieldCheck,
  ExternalLink,
} from "lucide-react";

import { OrganizationFilter, useOrganizationFilter } from "@/components/admin/OrganizationFilter";
import { LIDER_IA_COMPONENTS, DIMENSION_COLORS as LIDER_DIMENSION_COLORS, DIMENSION_NAMES as LIDER_DIMENSION_NAMES, CLASSIFICATION_MATRIX as LIDER_CLASSIFICATION } from "@/data/liderIAEvaluationData";
import { useEvaluationWeights } from "@/hooks/useEvaluationWeights";
import { BENEFIT_TYPE_LABELS, COST_LABELS, calculateROI, calculateBCR, calculatePaybackMonths, type ROICostStructure } from "@/data/roiModelData";
import { usePortfolioProjects, getROISummaries, type PortfolioProject } from "@/hooks/usePortfolioProjects";
import { SOLUTION_TYPES } from "@/data/constants";
import { supabase } from "@/integrations/supabase/client";
import * as XLSX from "xlsx";

import { PortfolioScatterChart, ClassificationPieChart, TopROIBarChart } from "@/components/admin/portfolio/PortfolioCharts";
import { ProjectComparisonDialog } from "@/components/admin/portfolio/ProjectComparisonDialog";
import { ProjectCardGrid } from "@/components/admin/portfolio/ProjectCardGrid";
import { BatchEvaluateDialog, type BatchEvalItem } from "@/components/admin/portfolio/BatchEvaluateDialog";
import { Brain } from "lucide-react";
import { useOrgFilterStore } from '@/hooks/useOrgFilterStore';
import { usePortfolioFilters } from '@/hooks/usePortfolioFiltersStore';
import { useOrgCurrency } from '@/hooks/useOrgCurrency';
import { formatCurrency as fmtCurrency } from '@/lib/formatCurrency';
import { fetchBulkProjectExportData } from '@/hooks/useBulkProjectExport';
import { generateMultiProjectPDF } from '@/lib/generateProjectPDF';
import { toast } from '@/hooks/use-toast';
import { FileDown } from 'lucide-react';
import { signDeliverableUrl } from '@/lib/signDeliverable';

// ── Status config ──
const statusLabels: Record<string, string> = {
  ideation: "Ideación", in_progress: "En progreso", completed: "Completado", paused: "Pausado",
};
const statusColors: Record<string, string> = {
  ideation: "bg-info/20 text-info border-info/30",
  in_progress: "bg-warning/20 text-warning border-warning/30",
  completed: "bg-success/20 text-success border-success/30",
  paused: "bg-muted text-muted-foreground border-border",
};
const statusIcons: Record<string, React.ReactNode> = {
  ideation: <Lightbulb className="w-4 h-4" />,
  in_progress: <Rocket className="w-4 h-4" />,
  completed: <Factory className="w-4 h-4" />,
};

function getClassification(score: number) {
  if (score >= 400) return "Quick Win";
  if (score >= 300) return "Strategic Play";
  if (score >= 200) return "Experiment";
  return "Back-log";
}

interface SynergyResult {
  projectAId: string;
  projectAName: string;
  projectACompany: string | null;
  projectBId: string;
  projectBName: string;
  projectBCompany: string | null;
  similarity: string;
  reason: string;
  potentialSaving: string;
  action: string;
}

// formatCurrency is now resolved per-org inside the component (default COP).

const ITEMS_PER_PAGE = 15;

export default function AIPortfolioPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, currentRole } = useAuth();
  const isAuditor = currentRole === 'auditor'; // solo lectura: ocultar acciones IA/aprobación
  const [filters, setFilters] = usePortfolioFilters();
  const {
    searchTerm, selectedCohort, selectedArea, selectedStatus,
    selectedClassification, showSubmittedOnly, selectedDeliverable,
    selectedEvaluated, sortBy, viewMode, currentPage,
  } = filters;
  const setSearchTerm = (v: string) => setFilters({ searchTerm: v });
  const setSelectedCohort = (v: string) => setFilters({ selectedCohort: v });
  const setSelectedArea = (v: string) => setFilters({ selectedArea: v });
  const setSelectedStatus = (v: string) => setFilters({ selectedStatus: v });
  const setSelectedClassification = (v: string) => setFilters({ selectedClassification: v });
  const setShowSubmittedOnly = (v: boolean) => setFilters({ showSubmittedOnly: v });
  const setSelectedDeliverable = (v: 'all' | 'with' | 'without') => setFilters({ selectedDeliverable: v });
  const setSelectedEvaluated = (v: 'all' | 'evaluated' | 'not_evaluated') => setFilters({ selectedEvaluated: v });
  const setSortBy = (v: 'score' | 'roi' | 'cohort' | 'fecha') => setFilters({ sortBy: v });
  const setViewMode = (v: 'table' | 'cards') => setFilters({ viewMode: v });
  const setCurrentPage = (v: number | ((prev: number) => number)) =>
    setFilters({ currentPage: typeof v === 'function' ? (v as (p: number) => number)(currentPage) : v });
  const [selectedOrg, setSelectedOrg] = useOrgFilterStore();
  const currency = useOrgCurrency(selectedOrg);
  const formatCurrency = (v: number) => fmtCurrency(v, currency);

  // Comparison state
  const [selectedForCompare, setSelectedForCompare] = useState<Set<string>>(new Set());
  const [showComparison, setShowComparison] = useState(false);
  const [showBatchEval, setShowBatchEval] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const handleExportSelectedPDF = async () => {
    const ids = Array.from(selectedForCompare);
    if (ids.length === 0) return;
    setExportingPdf(true);
    try {
      const inputs = await fetchBulkProjectExportData(ids);
      if (inputs.length === 0) {
        toast({ title: 'Sin datos', description: 'No se encontraron proyectos para exportar.', variant: 'destructive' });
        return;
      }
      await generateMultiProjectPDF(inputs);
      toast({ title: 'PDF generado', description: `${inputs.length} proyecto${inputs.length === 1 ? '' : 's'} exportado${inputs.length === 1 ? '' : 's'}.` });
    } catch (e: any) {
      console.error('Bulk PDF export failed', e);
      toast({ title: 'Error al exportar', description: e?.message || 'No se pudo generar el PDF.', variant: 'destructive' });
    } finally {
      setExportingPdf(false);
    }
  };

  // Synergy state
  const [synergyAnalyzing, setSynergyAnalyzing] = useState(false);
  const [synergyResults, setSynergyResults] = useState<SynergyResult[] | null>(null);

  // Diálogo de rechazo (motivo)
  const [rejectTarget, setRejectTarget] = useState<{ id: string; name: string } | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const { isSuperAdmin, effectiveOrg, showAllOrgs, getOrgName } = useOrganizationFilter(selectedOrg);

  // Revisión de proyecto: aprobar / rechazar / reiniciar (tri-estado).
  // approved_at seteado = aprobado · rejected_at seteado = rechazado · ambos null = pendiente.
  const reviewMutation = useMutation({
    mutationFn: async ({ id, action, reason }: { id: string; action: 'approve' | 'reject' | 'clear'; reason?: string }) => {
      const nowIso = new Date().toISOString();
      const patch =
        action === 'approve'
          ? { approved_at: nowIso, approved_by: user?.id ?? null, rejected_at: null, rejected_by: null, rejection_reason: null }
          : action === 'reject'
          ? { approved_at: null, approved_by: null, rejected_at: nowIso, rejected_by: user?.id ?? null, rejection_reason: reason?.trim() || null }
          : { approved_at: null, approved_by: null, rejected_at: null, rejected_by: null, rejection_reason: null };
      const { data, error } = await supabase
        .from('participant_projects')
        .update(patch as any)
        .eq('id', id)
        .select('id');
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('Sin permisos para actualizar (RLS bloqueó el UPDATE). Verifica que tu rol activo sea super_admin.');
      }
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['portfolio-projects'] });
      toast({
        title: vars.action === 'approve' ? 'Proyecto aprobado'
          : vars.action === 'reject' ? 'Proyecto rechazado'
          : 'Revisión reiniciada',
      });
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err?.message || 'No se pudo actualizar la revisión', variant: 'destructive' });
    },
  });

  const downloadDeliverable = async (p: PortfolioProject) => {
    const stored = p.fileUrl || (p.fileUrls && p.fileUrls[0]) || null;
    if (!stored) return;
    try {
      const signed = await signDeliverableUrl(stored);
      window.open(signed, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      console.warn('downloadDeliverable failed', { stored, err });
      toast({
        title: 'No se pudo descargar el entregable',
        description: err?.message || 'Verifica permisos o si el archivo existe.',
        variant: 'destructive',
      });
    }
  };
  const { getWeight, isCustom: isCustomWeights } = useEvaluationWeights(effectiveOrg);

  const { data: allProjects = [], isLoading } = usePortfolioProjects(
    showAllOrgs ? undefined : effectiveOrg || undefined
  );

  // Todas las cohortes (aunque no tengan proyectos aún) para el filtro.
  const { data: allCohortNames = [] } = useQuery({
    queryKey: ['portfolio-cohort-names', showAllOrgs ? 'all' : effectiveOrg],
    queryFn: async () => {
      const { data, error } = await supabase.from('cohorts').select('name, organization_id');
      if (error) throw error;
      let rows = data ?? [];
      if (!showAllOrgs && effectiveOrg) rows = rows.filter((r: any) => r.organization_id === effectiveOrg);
      return rows.map((r: any) => r.name).filter(Boolean) as string[];
    },
  });

  // Dynamic filter options — une cohortes con proyectos + todas las cohortes.
  const cohorts = useMemo(() => {
    const set = new Set<string>();
    allProjects.forEach(p => { if (p.cohortName) set.add(p.cohortName); });
    // El auditor NO suma allCohortNames (incluiría cohortes públicas por RLS);
    // sus opciones salen de los proyectos ya scopeados a sus cohortes asignadas.
    if (!isAuditor) allCohortNames.forEach(n => { if (n) set.add(n); });
    return ["Todas", ...Array.from(set).sort((a, b) => a.localeCompare(b))] as string[];
  }, [allProjects, allCohortNames, isAuditor]);

  const areas = useMemo(() => {
    const set = new Set(allProjects.map(p => p.area).filter(Boolean));
    return ["Todas", ...Array.from(set)] as string[];
  }, [allProjects]);

  const statuses = useMemo(() => {
    const set = new Set(allProjects.map(p => p.status));
    return ["Todos", ...Array.from(set)];
  }, [allProjects]);

  // Filter
  const filteredProjects = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    return allProjects.filter((p) => {
      const collabHaystack = (p.collaborators || [])
        .map((c) => `${c.name || ''} ${c.email || ''}`)
        .join(' ')
        .toLowerCase();
      const matchesSearch = !q ||
        p.name.toLowerCase().includes(q) ||
        (p.company || "").toLowerCase().includes(q) ||
        (p.participantName || "").toLowerCase().includes(q) ||
        (p.participantEmail || "").toLowerCase().includes(q) ||
        collabHaystack.includes(q);
      const matchesCohort = selectedCohort === "Todas" || p.cohortName === selectedCohort;
      const matchesArea = selectedArea === "Todas" || p.area === selectedArea;
      const matchesStatus = selectedStatus === "Todos" || p.status === selectedStatus;
      const matchesClassification = selectedClassification === "Todas" || getClassification(p.totalScore) === selectedClassification;
      const matchesSubmitted = !showSubmittedOnly || p.submitted;
      const matchesDeliverable =
        selectedDeliverable === "all" ||
        (selectedDeliverable === "with" && p.hasDeliverable) ||
        (selectedDeliverable === "without" && !p.hasDeliverable);
      const matchesEvaluated =
        selectedEvaluated === "all" ||
        (selectedEvaluated === "evaluated" && p.hasEvaluation) ||
        (selectedEvaluated === "not_evaluated" && !p.hasEvaluation);
      return matchesSearch && matchesCohort && matchesArea && matchesStatus && matchesClassification && matchesSubmitted && matchesDeliverable && matchesEvaluated;
    });
  }, [allProjects, searchTerm, selectedCohort, selectedArea, selectedStatus, selectedClassification, showSubmittedOnly, selectedDeliverable, selectedEvaluated]);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [searchTerm, selectedCohort, selectedArea, selectedStatus, selectedClassification, showSubmittedOnly, selectedDeliverable, selectedEvaluated]);

  const sortedProjects = useMemo(() => {
    return [...filteredProjects].sort((a, b) => {
      if (sortBy === "score") return b.totalScore - a.totalScore;
      if (sortBy === "roi") {
        const getROIValue = (rd: typeof a.roiData) => {
          if (!rd || !rd.costs || !rd.benefits) return 0;
          const benefits = Array.isArray(rd.benefits) ? rd.benefits.reduce((s, bn) => s + (bn.type === "not_defined" ? 0 : bn.amount), 0) : 0;
          const costs = (rd.costs.subscriptions || 0) + (rd.costs.tokenConsumption || 0) + (rd.costs.personnel || 0) + (rd.costs.training || 0);
          return calculateROI(benefits, costs);
        };
        const roiA = getROIValue(a.roiData);
        const roiB = getROIValue(b.roiData);
        return roiB - roiA;
      }
      if (sortBy === "cohort") return (b.cohortName || "").localeCompare(a.cohortName || "");
      if (sortBy === "fecha") return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      return 0;
    });
  }, [filteredProjects, sortBy]);

  // Pagination
  const totalPages = Math.ceil(sortedProjects.length / ITEMS_PER_PAGE);
  const paginatedProjects = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedProjects.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedProjects, currentPage]);

  // Una fila por proyecto (los colaboradores se muestran dentro de la celda del participante)


  // Stats
  const totalProjects = allProjects.length;
  const projectsByStatus = useMemo(() => ({
    ideation: allProjects.filter(p => p.status === "ideation").length,
    in_progress: allProjects.filter(p => p.status === "in_progress").length,
    completed: allProjects.filter(p => p.status === "completed").length,
  }), [allProjects]);
  const avgScore = totalProjects > 0 ? Math.round(allProjects.reduce((s, p) => s + p.totalScore, 0) / totalProjects) : 0;
  const activeCohorts = new Set(allProjects.map(p => p.cohortName).filter(Boolean)).size;

  // ROI data
  const roiSummaries = useMemo(() => getROISummaries(allProjects), [allProjects]);
  const totalROICosts = roiSummaries.reduce((s, p) => s + p.totalCosts, 0);
  const totalROIBenefits = roiSummaries.reduce((s, p) => s + p.totalBenefits, 0);

  // Comparison toggle
  const toggleCompareSelect = (id: string) => {
    setSelectedForCompare(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const comparisonProjects = useMemo(
    () => allProjects.filter(p => selectedForCompare.has(p.id)),
    [allProjects, selectedForCompare],
  );

  // Export to Excel (enhanced with ROI sheet)
  const exportToExcel = () => {
    const rows = sortedProjects.map(p => ({
      Proyecto: p.name,
      Participante: p.participantName || "",
      Colaboradores: (p.collaborators || []).map(c => c.name || c.email).filter(Boolean).join(", "),
      Empresa: p.company || "",
      Área: p.area || "",
      Cohorte: p.cohortName || "",
      Estado: statusLabels[p.status] || p.status,
      Entregable: p.hasDeliverable ? "Sí" : "No",
      "Contestó todas las preguntas": p.answeredAll ? "Sí" : "No",
      "Score LIDER-IA": p.totalScore,
      Clasificación: getClassification(p.totalScore),
      "Fecha Entrega": p.status === "completed" ? (p.submittedAt?.split("T")[0] || "") : "",
      Herramientas: p.tools.join(", "),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Portafolio");

    // ROI sheet
    if (roiSummaries.length > 0) {
      const roiRows = roiSummaries.map(p => ({
        Proyecto: p.projectName,
        Empresa: p.company,
        "Suscripciones": p.costs.subscriptions,
        "Tokens": p.costs.tokenConsumption,
        "Personal": p.costs.personnel,
        "Capacitación": p.costs.training,
        "Total Costos": p.totalCosts,
        "Total Beneficios": p.totalBenefits,
        "ROI %": Math.round(p.roi),
        "BCR": Number(p.bcr.toFixed(2)),
        "Payback (meses)": p.paybackMonths === Infinity ? "∞" : Number(p.paybackMonths.toFixed(1)),
      }));
      const wsRoi = XLSX.utils.json_to_sheet(roiRows);
      XLSX.utils.book_append_sheet(wb, wsRoi, "ROI Detallado");
    }

    XLSX.writeFile(wb, "portafolio-proyectos-ia.xlsx");
  };

  // Synergy analysis
  const analyzeSynergies = async () => {
    setSynergyAnalyzing(true);
    setSynergyResults(null);
    try {
      const projectsForAI = allProjects.map(p => ({
        id: p.id, name: p.name, problem: p.problem, area: p.area,
        company: p.company, tools: p.tools, solutionDescription: p.solutionDescription,
      }));
      const { data, error } = await supabase.functions.invoke("analyze-synergies", {
        body: { projects: projectsForAI },
      });
      if (error) throw error;
      setSynergyResults(data?.synergies || []);
    } catch (err: any) {
      console.error("Synergy analysis error:", err);
      setSynergyResults([]);
    } finally {
      setSynergyAnalyzing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">
            Gestión de Portafolios de Proyectos IA
          </h1>
          <p className="text-muted-foreground mt-1">
            AI Portfolio Intelligence {isSuperAdmin && selectedOrg !== "all" && `- ${getOrgName()}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={exportToExcel} className="gap-2">
            <Download className="w-4 h-4" />
            Exportar Excel
          </Button>
          <OrganizationFilter selectedOrg={selectedOrg} onOrgChange={setSelectedOrg} />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Proyectos</p>
                <p className="text-3xl font-bold text-foreground">{totalProjects}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                <Layers className="w-6 h-6 text-primary" />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Badge variant="outline" className="text-xs">
                <Lightbulb className="w-3 h-3 mr-1" />{projectsByStatus.ideation}
              </Badge>
              <Badge variant="outline" className="text-xs">
                <Rocket className="w-3 h-3 mr-1" />{projectsByStatus.in_progress}
              </Badge>
              <Badge variant="outline" className="text-xs">
                <Factory className="w-3 h-3 mr-1" />{projectsByStatus.completed}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Beneficio Proyectado</p>
                <p className="text-2xl font-bold text-success">
                  {roiSummaries.length > 0 ? formatCurrency(totalROIBenefits - totalROICosts) : "Sin datos ROI"}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-success/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-success" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              {roiSummaries.length} proyecto{roiSummaries.length !== 1 ? "s" : ""} con datos de ROI
            </p>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Score Promedio</p>
                <p className="text-3xl font-bold text-foreground">{avgScore}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-warning/20 flex items-center justify-center">
                <Target className="w-6 h-6 text-warning" />
              </div>
            </div>
            <Progress value={(avgScore / 500) * 100} className="mt-4 h-2" />
            <p className="text-xs text-muted-foreground mt-1">Máximo: 500 puntos</p>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Cohortes Activas</p>
                <p className="text-3xl font-bold text-foreground">{activeCohorts}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-info/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-info" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-4 text-sm text-muted-foreground">
              <Layers className="w-4 h-4" />
              <span>{totalProjects} proyectos en total</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="portfolio" className="space-y-6">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="portfolio" className="gap-2"><Layers className="w-4 h-4" />Portafolio</TabsTrigger>
          <TabsTrigger value="evaluation" className="gap-2"><BarChart3 className="w-4 h-4" />Framework</TabsTrigger>
          <TabsTrigger value="roi" className="gap-2"><DollarSign className="w-4 h-4" />Análisis de ROI</TabsTrigger>
          <TabsTrigger value="synergies" className="gap-2"><GitMerge className="w-4 h-4" />Sinergias</TabsTrigger>
        </TabsList>

        {/* ── Portfolio Tab ── */}
        <TabsContent value="portfolio" className="space-y-6">
          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Buscar proyecto, empresa, participante o colaborador..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
                  </div>
                </div>
                <Select value={selectedCohort} onValueChange={setSelectedCohort}>
                  <SelectTrigger className="w-[160px]"><Calendar className="w-4 h-4 mr-2" /><SelectValue /></SelectTrigger>
                  <SelectContent>{cohorts.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={selectedArea} onValueChange={setSelectedArea}>
                  <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                  <SelectContent>{areas.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="w-[150px]"><Zap className="w-4 h-4 mr-2" /><SelectValue /></SelectTrigger>
                  <SelectContent>{statuses.map(s => <SelectItem key={s} value={s}>{s === "Todos" ? "Todos" : statusLabels[s] || s}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={selectedClassification} onValueChange={setSelectedClassification}>
                  <SelectTrigger className="w-[160px]"><Target className="w-4 h-4 mr-2" /><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Todas">Todas</SelectItem>
                    <SelectItem value="Quick Win">Quick Win</SelectItem>
                    <SelectItem value="Strategic Play">Strategic Play</SelectItem>
                    <SelectItem value="Experiment">Experiment</SelectItem>
                    <SelectItem value="Back-log">Back-log</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={selectedEvaluated} onValueChange={(v) => setSelectedEvaluated(v as any)}>
                  <SelectTrigger className="w-[180px]"><CheckCircle2 className="w-4 h-4 mr-2" /><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Evaluación: Todos</SelectItem>
                    <SelectItem value="evaluated">Evaluados IA</SelectItem>
                    <SelectItem value="not_evaluated">Sin evaluar</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={selectedDeliverable} onValueChange={(v) => setSelectedDeliverable(v as any)}>
                  <SelectTrigger className="w-[180px]"><Paperclip className="w-4 h-4 mr-2" /><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Entregable: Todos</SelectItem>
                    <SelectItem value="with">Con entregable</SelectItem>
                    <SelectItem value="without">Sin entregable</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={showSubmittedOnly ? "submitted" : "all"} onValueChange={(v) => setShowSubmittedOnly(v === "submitted")}>
                  <SelectTrigger className="w-[170px]"><FileCheck2 className="w-4 h-4 mr-2" /><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Entrega: Todos</SelectItem>
                    <SelectItem value="submitted">Solo entregados</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
                  <SelectTrigger className="w-[160px]"><BarChart3 className="w-4 h-4 mr-2" /><SelectValue placeholder="Ordenar por" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="score">Score LIDER-IA</SelectItem>
                    <SelectItem value="roi">ROI</SelectItem>
                    <SelectItem value="cohort">Cohorte</SelectItem>
                    <SelectItem value="fecha">Fecha</SelectItem>
                  </SelectContent>
                </Select>

                {/* View toggle */}
                <div className="flex items-center gap-1 border border-border rounded-lg p-1">
                  <Button
                    variant={viewMode === "table" ? "default" : "ghost"}
                    size="icon" className="h-8 w-8"
                    onClick={() => setViewMode("table")}
                  >
                    <List className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={viewMode === "cards" ? "default" : "ghost"}
                    size="icon" className="h-8 w-8"
                    onClick={() => setViewMode("cards")}
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Selection bar */}
          {selectedForCompare.size > 0 && (() => {
            const filteredIds = sortedProjects.map(p => p.id);
            const allSelected = filteredIds.length > 0 && filteredIds.every(id => selectedForCompare.has(id));
            const toggleAll = () => {
              setSelectedForCompare(prev => {
                const next = new Set(prev);
                if (allSelected) filteredIds.forEach(id => next.delete(id));
                else filteredIds.forEach(id => next.add(id));
                return next;
              });
            };
            return (
              <div className="flex flex-wrap items-center gap-3 p-3 rounded-xl bg-primary/10 border border-primary/20">
                <GitCompare className="w-5 h-5 text-primary flex-shrink-0" />
                <p className="text-sm text-foreground flex-1 min-w-[180px]">
                  <strong>{selectedForCompare.size}</strong> proyecto{selectedForCompare.size === 1 ? "" : "s"} seleccionado{selectedForCompare.size === 1 ? "" : "s"}
                </p>
                <Button size="sm" variant="outline" onClick={toggleAll} className="gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  {allSelected ? "Quitar todos" : `Seleccionar todos (${filteredIds.length})`}
                </Button>
                {!isAuditor && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowBatchEval(true)}
                    disabled={selectedForCompare.size < 1}
                    className="gap-2"
                  >
                    <Brain className="w-4 h-4" />
                    Evaluar con IA
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={() => setShowComparison(true)}
                  disabled={selectedForCompare.size < 2 || selectedForCompare.size > 3}
                  title={selectedForCompare.size > 3 ? "Máx. 3 para comparar" : undefined}
                  className="gap-2"
                >
                  <GitCompare className="w-4 h-4" />
                  Comparar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleExportSelectedPDF}
                  disabled={exportingPdf || selectedForCompare.size < 1}
                  className="gap-2"
                  title="Descargar un PDF con la información completa de los proyectos seleccionados"
                >
                  {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                  Descargar PDF ({selectedForCompare.size})
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedForCompare(new Set())} disabled={selectedForCompare.size === 0}>
                  Limpiar
                </Button>
              </div>
            );
          })()}

          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Proyectos del Portafolio</span>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{sortedProjects.length} proyectos</Badge>
                  {totalPages > 1 && (
                    <Badge variant="outline" className="text-xs">
                      Pág. {currentPage} de {totalPages}
                    </Badge>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {viewMode === "cards" ? (
                <ProjectCardGrid
                  projects={paginatedProjects}
                  selectedIds={selectedForCompare}
                  onToggleSelect={toggleCompareSelect}
                  onNavigate={(id) => navigate(`/ai-portfolio/${id}`)}
                />
              ) : sortedProjects.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Layers className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>No hay proyectos entregados aún</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={sortedProjects.length > 0 && sortedProjects.every(p => selectedForCompare.has(p.id))}
                          onCheckedChange={() => {
                            const ids = sortedProjects.map(p => p.id);
                            const allSel = ids.length > 0 && ids.every(id => selectedForCompare.has(id));
                            setSelectedForCompare(prev => {
                              const next = new Set(prev);
                              if (allSel) ids.forEach(id => next.delete(id));
                              else ids.forEach(id => next.add(id));
                              return next;
                            });
                          }}
                          aria-label="Seleccionar todos"
                        />
                      </TableHead>
                      <TableHead>Proyecto</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Participante</TableHead>
                      <TableHead>Colaboradores</TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Cohorte</TableHead>
                      <TableHead>Área</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Entregado</TableHead>
                      <TableHead>Entregable</TableHead>
                      <TableHead className="text-center">Score</TableHead>
                      <TableHead>Clasificación</TableHead>
                      {isSuperAdmin && <TableHead className="text-center">Aprobación</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedProjects.map((p) => {
                      return (
                      <TableRow key={p.id} className="hover:bg-muted/50">
                        <TableCell onClick={e => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedForCompare.has(p.id)}
                            onCheckedChange={() => toggleCompareSelect(p.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <a
                            href={`/ai-portfolio/${p.id}`}
                            onClick={(e) => {
                              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                              e.preventDefault();
                              navigate(`/ai-portfolio/${p.id}`);
                            }}
                            className="flex items-center gap-2 cursor-pointer no-underline text-inherit"
                          >
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Sparkles className="w-4 h-4 text-primary" />
                            </div>
                            <div>
                              <div className="flex items-center gap-1.5">
                                <p className="font-medium text-foreground">{p.name}</p>
                                <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    window.open(`/ai-portfolio/${p.id}`, '_blank', 'noopener,noreferrer');
                                  }}
                                  className="text-muted-foreground hover:text-primary"
                                  title="Abrir en nueva pestaña"
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </button>
                              </div>
                              {p.status === "completed" && p.submittedAt && (
                                <p className="text-xs text-muted-foreground">
                                  Entregado: {p.submittedAt.split("T")[0]}
                                </p>
                              )}
                            </div>
                          </a>
                        </TableCell>
                        <TableCell>
                          {p.solutionType && SOLUTION_TYPES[p.solutionType as 1|2|3|4|5] ? (
                            <Badge variant="outline" className="text-xs gap-1">
                              <span>{SOLUTION_TYPES[p.solutionType as 1|2|3|4|5].icon}</span>
                              <span>{SOLUTION_TYPES[p.solutionType as 1|2|3|4|5].name}</span>
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          <div>
                            <p className="font-medium">{p.participantName || '—'}</p>
                            {p.participantEmail && (
                              <p className="text-xs text-muted-foreground">{p.participantEmail}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm max-w-[240px]">
                          {p.collaborators && p.collaborators.length > 0 ? (
                            <div
                              className="flex flex-col gap-0.5"
                              title={p.collaborators.map(c => `${c.name || '(sin nombre)'}${c.email ? ` <${c.email}>` : ''}`).join('\n')}
                            >
                              {p.collaborators.slice(0, 3).map((c, i) => (
                                <span key={c.userId || i} className="text-xs truncate">
                                  {c.name || c.email || '—'}
                                </span>
                              ))}
                              {p.collaborators.length > 3 && (
                                <span className="text-[10px] text-muted-foreground">
                                  +{p.collaborators.length - 3} más
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell>{p.company || "—"}</TableCell>
                        <TableCell>
                          {p.cohortName ? <Badge variant="outline">{p.cohortName}</Badge> : "—"}
                        </TableCell>
                        <TableCell>{p.area || "—"}</TableCell>
                        <TableCell>
                          <Badge className={`${statusColors[p.status] || ""} border gap-1`}>
                            {statusIcons[p.status]}
                            {statusLabels[p.status] || p.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {p.submitted ? (
                            <Badge className="bg-success/20 text-success border-success/30 border gap-1 text-xs">
                              <CheckCircle2 className="w-3 h-3" />
                              Entregado
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground gap-1 text-xs">
                              Borrador
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {p.hasDeliverable ? (
                            <div className="flex items-center gap-2">
                              <Badge className="bg-success/20 text-success border-success/30 border gap-1 text-xs">
                                <FileCheck2 className="w-3 h-3" />
                                Cargado{p.deliverableCount > 1 ? ` (${p.deliverableCount})` : ""}
                              </Badge>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={(e) => { e.stopPropagation(); downloadDeliverable(p); }}
                                title="Descargar entregable"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground gap-1 text-xs">
                              <FileX2 className="w-3 h-3" />
                              Sin entregar
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Progress value={(p.totalScore / 500) * 100} className="w-16 h-2" />
                            <span className="font-bold text-foreground">{p.totalScore}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{getClassification(p.totalScore)}</Badge>
                        </TableCell>
                        {isSuperAdmin && (
                          <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                            {p.approvedAt ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-1 bg-success/10 text-success border-success/30 hover:bg-success/20"
                                disabled={reviewMutation.isPending}
                                onClick={() => reviewMutation.mutate({ id: p.id, action: 'clear' })}
                                title={`Aprobado el ${p.approvedAt.split('T')[0]} — clic para reiniciar`}
                              >
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Aprobado
                              </Button>
                            ) : p.rejectedAt ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-1 bg-destructive/10 text-destructive border-destructive/30 hover:bg-destructive/20"
                                disabled={reviewMutation.isPending}
                                onClick={() => reviewMutation.mutate({ id: p.id, action: 'clear' })}
                                title={`Rechazado el ${p.rejectedAt.split('T')[0]}${p.rejectionReason ? ` — ${p.rejectionReason}` : ''} — clic para reiniciar`}
                              >
                                <FileX2 className="w-3.5 h-3.5" />
                                Rechazado
                              </Button>
                            ) : (
                              <div className="flex gap-1 justify-center">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="gap-1"
                                  disabled={reviewMutation.isPending}
                                  onClick={() => reviewMutation.mutate({ id: p.id, action: 'approve' })}
                                >
                                  <ShieldCheck className="w-3.5 h-3.5" />
                                  Aprobar
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="gap-1 text-destructive border-destructive/30 hover:bg-destructive/10"
                                  disabled={reviewMutation.isPending}
                                  onClick={() => { setRejectTarget({ id: p.id, name: p.name }); setRejectReason(''); }}
                                >
                                  <FileX2 className="w-3.5 h-3.5" />
                                  Rechazar
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                    })}
                  </TableBody>
                </Table>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-4">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        />
                      </PaginationItem>
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        let page: number;
                        if (totalPages <= 5) page = i + 1;
                        else if (currentPage <= 3) page = i + 1;
                        else if (currentPage >= totalPages - 2) page = totalPages - 4 + i;
                        else page = currentPage - 2 + i;
                        return (
                          <PaginationItem key={page}>
                            <PaginationLink
                              isActive={page === currentPage}
                              onClick={() => setCurrentPage(page)}
                              className="cursor-pointer"
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      })}
                      <PaginationItem>
                        <PaginationNext
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Evaluation Framework Tab ── */}
        <TabsContent value="evaluation" className="space-y-6">
          {/* Recharts scatter chart replaces old CSS-dot matrix */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PortfolioScatterChart projects={allProjects} />
            <ClassificationPieChart projects={allProjects} />
          </div>

          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="w-5 h-5 text-primary" />
                Matriz de Priorización y Acción
              </CardTitle>
              <CardDescription>Basado en el puntaje total (0-500)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {LIDER_CLASSIFICATION.map(item => (
                  <div key={item.classification} className={`p-4 rounded-xl border ${item.color}`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold">{item.classification}</h4>
                      <Badge variant="outline" className="text-xs">{item.range} pts</Badge>
                    </div>
                    <p className="text-sm">{item.action}</p>
                    <p className="text-xs mt-2 font-medium">
                      {allProjects.filter(p => getClassification(p.totalScore) === item.classification).length} proyectos
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Marco de Evaluación LIDER-IA — 12 Componentes
                {isCustomWeights && (
                  <Badge variant="secondary" className="text-xs ml-2">Pesos personalizados</Badge>
                )}
              </CardTitle>
              <CardDescription>Escala 1-5 por componente, peso ponderado.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {(["L", "I", "D", "E", "R"] as const).map(dim => {
                const dimComponents = LIDER_IA_COMPONENTS.filter(c => c.dimensionLetter === dim);
                return (
                  <div key={dim}>
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className={`${LIDER_DIMENSION_COLORS[dim]} border-0 font-bold text-sm`}>{dim}</Badge>
                      <h3 className="font-semibold text-foreground">{LIDER_DIMENSION_NAMES[dim]}</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-4">
                      {dimComponents.map(comp => (
                        <div key={comp.id} className="flex items-start gap-3 p-4 rounded-xl bg-muted/50">
                          <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
                            <Target className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-foreground text-sm">{comp.name}</h4>
                              <Badge variant="outline" className="text-xs">{getWeight(comp.id)}%</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">{comp.description}</p>
                            <p className="text-xs text-primary/70 mt-1 italic">Nivel 5: {comp.level5}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── ROI Tab ── */}
        <TabsContent value="roi" className="space-y-6">
          {roiSummaries.length === 0 ? (
            <Card className="glass-card">
              <CardContent className="p-12 text-center">
                <DollarSign className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-40" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Sin datos de ROI</h3>
                <p className="text-muted-foreground">Los participantes aún no han ingresado datos de ROI en sus proyectos.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="glass-card">
                  <CardContent className="p-6">
                    <p className="text-sm text-muted-foreground">Inversión Total</p>
                    <p className="text-2xl font-bold text-destructive">{formatCurrency(totalROICosts)}</p>
                    <p className="text-xs text-muted-foreground mt-2">Costos consolidados anuales</p>
                  </CardContent>
                </Card>
                <Card className="glass-card">
                  <CardContent className="p-6">
                    <p className="text-sm text-muted-foreground">Beneficios Totales</p>
                    <p className="text-2xl font-bold text-success">{formatCurrency(totalROIBenefits)}</p>
                    <p className="text-xs text-muted-foreground mt-2">Retorno anual estimado</p>
                  </CardContent>
                </Card>
                <Card className="glass-card">
                  <CardContent className="p-6">
                    <p className="text-sm text-muted-foreground">ROI</p>
                    <p className="text-2xl font-bold text-success">{Math.round(calculateROI(totalROIBenefits, totalROICosts))}%</p>
                    <p className="text-xs text-muted-foreground mt-2">BCR: {calculateBCR(totalROIBenefits, totalROICosts).toFixed(2)}x</p>
                  </CardContent>
                </Card>
                <Card className="glass-card">
                  <CardContent className="p-6">
                    <p className="text-sm text-muted-foreground">Payback Promedio</p>
                    <p className="text-2xl font-bold text-foreground">{calculatePaybackMonths(totalROIBenefits, totalROICosts).toFixed(1)} meses</p>
                    <p className="text-xs text-muted-foreground mt-2">Período de recuperación</p>
                  </CardContent>
                </Card>
              </div>

              {/* Top ROI bar chart */}
              <TopROIBarChart roiSummaries={roiSummaries} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ArrowDownRight className="w-5 h-5 text-destructive" />Desglose de Costos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {(Object.keys(COST_LABELS) as Array<keyof ROICostStructure>).map((key) => {
                        const total = roiSummaries.reduce((s, p) => s + p.costs[key], 0);
                        const pct = totalROICosts > 0 ? Math.round((total / totalROICosts) * 100) : 0;
                        return (
                          <div key={key} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span>{COST_LABELS[key].name}</span>
                              <span className="font-medium">{formatCurrency(total)} ({pct}%)</span>
                            </div>
                            <Progress value={pct} className="h-2" />
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ArrowUpRight className="w-5 h-5 text-success" />Desglose de Beneficios
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {(() => {
                        const map: Record<string, number> = {};
                        roiSummaries.forEach(p => {
                          p.benefits.forEach(b => {
                            if (b.type !== "not_defined") {
                              const label = BENEFIT_TYPE_LABELS[b.type];
                              map[label] = (map[label] || 0) + b.amount;
                            }
                          });
                        });
                        const totalBen = Object.values(map).reduce((a, b) => a + b, 0);
                        return Object.entries(map).map(([label, amount]) => {
                          const pct = totalBen > 0 ? Math.round((amount / totalBen) * 100) : 0;
                          return (
                            <div key={label} className="space-y-1">
                              <div className="flex justify-between text-sm">
                                <span>{label}</span>
                                <span className="font-medium">{formatCurrency(amount)} ({pct}%)</span>
                              </div>
                              <Progress value={pct} className="h-2" />
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-primary" />ROI por Proyecto
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Proyecto</TableHead>
                        <TableHead>Empresa</TableHead>
                        <TableHead className="text-right">Costos</TableHead>
                        <TableHead className="text-right">Beneficios</TableHead>
                        <TableHead className="text-right">ROI %</TableHead>
                        <TableHead className="text-right">BCR</TableHead>
                        <TableHead className="text-right">Payback</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {roiSummaries.map((p) => (
                        <TableRow key={p.projectId}>
                          <TableCell className="font-medium">{p.projectName}</TableCell>
                          <TableCell>{p.company}</TableCell>
                          <TableCell className="text-right text-destructive">{formatCurrency(p.totalCosts)}</TableCell>
                          <TableCell className="text-right text-success">{formatCurrency(p.totalBenefits)}</TableCell>
                          <TableCell className="text-right">
                            <Badge className={p.roi > 200 ? "bg-success/20 text-success" : "bg-warning/20 text-warning"}>
                              {Math.round(p.roi)}%
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">{p.bcr.toFixed(2)}x</TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {p.paybackMonths === Infinity ? "∞" : `${p.paybackMonths.toFixed(1)} meses`}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ── Synergies Tab ── */}
        <TabsContent value="synergies" className="space-y-6">
          <Card className="glass-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <GitMerge className="w-5 h-5 text-primary" />
                    Análisis de Sinergias entre Proyectos
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Usa IA para identificar proyectos con objetivos, tecnologías o datos similares que podrían unificarse.
                  </CardDescription>
                </div>
                {!isAuditor && (
                  <Button onClick={analyzeSynergies} disabled={synergyAnalyzing || allProjects.length < 2} className="gap-2">
                    {synergyAnalyzing ? (
                      <><Loader2 className="w-4 h-4 animate-spin" />Analizando...</>
                    ) : (
                      <><Sparkles className="w-4 h-4" />Analizar Sinergias</>
                    )}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!synergyResults && !synergyAnalyzing && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                    <GitMerge className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Detecta oportunidades de consolidación</h3>
                  <p className="text-muted-foreground max-w-md">
                    El análisis con IA evaluará los {allProjects.length} proyectos del portafolio para identificar pares con temáticas, tecnologías o datos en común.
                  </p>
                </div>
              )}

              {synergyAnalyzing && (
                <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
                  <Loader2 className="w-10 h-10 text-primary animate-spin" />
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">Analizando {allProjects.length} proyectos con IA...</h3>
                    <p className="text-muted-foreground text-sm mt-1">Comparando áreas, tecnologías, datos y objetivos</p>
                  </div>
                </div>
              )}

              {synergyResults && synergyResults.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-success/10 border border-success/20">
                    <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0" />
                    <p className="text-sm text-foreground">
                      Se identificaron <strong>{synergyResults.length} oportunidades de sinergia</strong>.
                    </p>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Proyecto A</TableHead>
                        <TableHead>Proyecto B</TableHead>
                        <TableHead>Similitud</TableHead>
                        <TableHead className="min-w-[300px]">Justificación</TableHead>
                        <TableHead>Ahorro Potencial</TableHead>
                        <TableHead className="min-w-[250px]">Acción Recomendada</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {synergyResults.map((rec, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <div className="space-y-1">
                              <p className="font-medium text-foreground text-sm">{rec.projectAName}</p>
                              <Badge variant="outline" className="text-xs">{rec.projectACompany || "—"}</Badge>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <p className="font-medium text-foreground text-sm">{rec.projectBName}</p>
                              <Badge variant="outline" className="text-xs">{rec.projectBCompany || "—"}</Badge>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={rec.similarity === "Alta"
                              ? "bg-success/20 text-success border-success/30 border"
                              : "bg-warning/20 text-warning border-warning/30 border"}>
                              {rec.similarity === "Alta" ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
                              {rec.similarity}
                            </Badge>
                          </TableCell>
                          <TableCell><p className="text-sm text-muted-foreground leading-relaxed">{rec.reason}</p></TableCell>
                          <TableCell><span className="text-sm font-semibold text-success">{rec.potentialSaving}</span></TableCell>
                          <TableCell><p className="text-sm font-medium text-foreground">{rec.action}</p></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {synergyResults && synergyResults.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <CheckCircle2 className="w-10 h-10 text-success mb-3" />
                  <h3 className="text-lg font-semibold text-foreground">No se encontraron sinergias</h3>
                  <p className="text-muted-foreground">Los proyectos del portafolio son suficientemente distintos entre sí.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Comparison Dialog */}
      <ProjectComparisonDialog
        open={showComparison}
        onOpenChange={setShowComparison}
        projects={comparisonProjects}
        currency={currency}
      />

      {/* Batch evaluation dialog */}
      <BatchEvaluateDialog
        open={showBatchEval}
        onOpenChange={setShowBatchEval}
        items={allProjects
          .filter(p => selectedForCompare.has(p.id))
          .map<BatchEvalItem>(p => ({ id: p.id, name: p.name, company: p.company }))}
      />

      {/* Reject dialog — motivo de rechazo */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) setRejectTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rechazar proyecto</DialogTitle>
            <DialogDescription>
              {rejectTarget ? `"${rejectTarget.name}" — ` : ''}escribí el motivo del rechazo. El participante lo verá en su panel para poder corregir y reenviar.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Motivo del rechazo (ej. falta detallar el impacto, el entregable no corresponde, etc.)"
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)} disabled={reviewMutation.isPending}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={reviewMutation.isPending || !rejectReason.trim()}
              onClick={() => {
                if (!rejectTarget) return;
                reviewMutation.mutate(
                  { id: rejectTarget.id, action: 'reject', reason: rejectReason },
                  { onSuccess: () => setRejectTarget(null) },
                );
              }}
            >
              {reviewMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileX2 className="w-4 h-4 mr-2" />}
              Rechazar proyecto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
